import React, { useEffect, useRef, useState } from 'react';
import { Layout, ConfigProvider, Menu, Drawer, Dropdown, Button, Avatar, Badge, theme } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuUnfoldOutlined, MenuFoldOutlined, ShoppingCartOutlined, WalletOutlined, GiftOutlined,
  UserOutlined, LogoutOutlined, SettingOutlined, MessageOutlined, HomeOutlined, AppstoreOutlined,
  SunOutlined, MoonOutlined, ArrowRightOutlined, ThunderboltOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Orders from './orders/Orders';
import Coupons from './coupons/Coupons';
import Balance from './balance/Balance';
import Settings from './settings/Settings';
import Overview from './overview/Overview';
import Chat from './message/Chat';
import Ventes from './ventes/Ventes';
import useUnreadChat from '../../hooks/useUnreadChat';
import './dashboardclient.css';

const { Header, Content, Sider } = Layout;

export default function ClientDashboard() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const root = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.innerWidth < 992);
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('client-panel-theme');
    return saved ? saved === 'dark' : localStorage.getItem('theme') !== 'is_light';
  });
  const activeTab = location.pathname.split('/')[2] || 'overview';
  const { count: unreadChat } = useUnreadChat();
  const languages = { ar: 'العربية', fr: 'Français', en: 'English', es: 'Español', tr: 'Türkçe', ru: 'Русский', pt: 'Português', nl: 'Nederlands', de: 'Deutsch', it: 'Italiano', zh: '中文' };
  const tabs = [
    { key: 'overview', label: t('Overview', { defaultValue: 'Overview' }), icon: <AppstoreOutlined /> },
    { key: 'orders', label: t('My Orders'), icon: <ShoppingCartOutlined /> },
    { key: 'ventes', label: t('My Sells'), icon: <ThunderboltOutlined /> },
    { key: 'coupons', label: t('My Coupons'), icon: <GiftOutlined /> },
    { key: 'balance', label: t('My Balance'), icon: <WalletOutlined /> },
    { key: 'settings', label: t('Profile Settings'), icon: <SettingOutlined /> },
    { key: 'chat', label: t('Support'),
      icon: <Badge dot={unreadChat > 0} offset={[2, -2]}><MessageOutlined /></Badge> },
  ];
  const title = tabs.find(tab => tab.key === activeTab)?.label || t('Overview', { defaultValue: 'Overview' });
  useEffect(() => {
    const media = window.matchMedia('(max-width: 991px)');
    const resize = () => { setMobile(media.matches); setMobileOpen(false); };
    media.addEventListener('change', resize);
    return () => media.removeEventListener('change', resize);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const toggleTheme = () => setDark(value => {
    localStorage.setItem('client-panel-theme', value ? 'light' : 'dark');
    return !value;
  });
  let storedUser = {};
  try { storedUser = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch { /* Use guest defaults. */ }
  const userData = { username: t('Guest User'), balance: 0, loyaltyPoints: 0, recentOrders: [], availableCoupons: [], ...storedUser };
  const handleTabChange = key => {
    setMobileOpen(false);
    navigate(key === 'home' ? '/' : key === 'overview' ? '/client-dashboard' : `/client-dashboard/${key}`);
  };
  const getStatusColor = status => ({ Delivered: 'success', Processing: 'warning', Shipped: 'processing' }[status] || 'default');
  const handleLogout = () => { localStorage.clear(); window.location.href = '/'; };
  const renderContent = () => {
    switch (activeTab) {
      case 'orders': return <Orders recentOrders={userData.recentOrders} getStatusColor={getStatusColor} />;
      case 'ventes': return <Ventes recentVentes={userData.recentVentes} getStatusColor={getStatusColor} />;
      case 'coupons': return <Coupons availableCoupons={userData.availableCoupons} />;
      case 'balance': return <Balance balance={userData.balance} loyaltyPoints={userData.loyaltyPoints} />;
      case 'settings': return <Settings photo_profil={userData.photo_profil} />;
      case 'chat': return <Chat />;
      default: return <Overview userData={userData} getStatusColor={getStatusColor} handleTabChange={handleTabChange} />;
    }
  };
  const themeControl = compact => <Button type="text" onClick={toggleTheme} icon={dark ? <SunOutlined /> : <MoonOutlined />}
    aria-label={t(dark ? 'Light Mode' : 'Dark Mode')} title={t(dark ? 'Light Mode' : 'Dark Mode')}>
    {!compact && t(dark ? 'Light Mode' : 'Dark Mode')}
  </Button>;
  const navigation = compact => <nav className="client-navigation" aria-label={t('Navigation', { defaultValue: 'Navigation' })}>
    <Link to="/client-dashboard" className="client-brand" onClick={() => setMobileOpen(false)} aria-label="MasterFut dashboard">
      <img className="client-brand-mark" src={`${process.env.PUBLIC_URL}/favicon.png`} alt="MasterFut" />{!compact && <span>Masterfut</span>}
    </Link>
    {!compact && <div className="client-nav-caption">{t('Your workspace', { defaultValue: 'Your workspace' })}</div>}
    <Menu mode="inline" inlineCollapsed={compact} selectedKeys={[activeTab]} items={tabs.map(tab => ({ ...tab,
      label: <Link to={tab.key === 'overview' ? '/client-dashboard' : `/client-dashboard/${tab.key}`} onClick={() => setMobileOpen(false)}>
        {tab.label}
        {tab.key === 'chat' && unreadChat > 0 && <Badge count={unreadChat} size="small" style={{ marginInlineStart: 8 }} />}
      </Link>
    }))} />
    <div className="client-navigation-bottom">
      {!compact && <div className="client-support-card"><MessageOutlined /><strong>{t('Need a hand?', { defaultValue: 'Need a hand?' })}</strong>
        <p>{t('Your next move starts here.', { defaultValue: 'Your next move starts here.' })}</p>
        <Button block onClick={() => handleTabChange('chat')}>{t('Support')} <ArrowRightOutlined /></Button></div>}
      <Button type="text" icon={<HomeOutlined />} onClick={() => handleTabChange('home')} title={t('Home')}>{!compact && t('Home')}</Button>
      {themeControl(compact)}
      <Button type="text" danger icon={<LogoutOutlined />} onClick={handleLogout} title={t('Logout')}>{!compact && t('Logout')}</Button>
    </div>
  </nav>;
  return <ConfigProvider modal={{ className: `client-panel client-panel-modal${dark ? ' client-dark' : ''}` }}
    theme={{ algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: { colorPrimary: dark ? '#b7f451' : '#427b0c', colorInfo: dark ? '#54dfd2' : '#087f83',
        colorBgLayout: dark ? '#0b1014' : '#f2f6f7', colorBgContainer: dark ? '#141c23' : '#ffffff',
        colorText: dark ? '#edf4f6' : '#172b37', colorTextSecondary: dark ? '#9baebc' : '#617580',
        colorBorder: dark ? '#33434e' : '#cfdee3', colorBorderSecondary: dark ? '#283741' : '#e1eaed',
        borderRadius: 12, fontFamily: 'Inter, system-ui, sans-serif' },
      components: { Button: { controlHeight: 42 }, Menu: { itemHeight: 46, itemBorderRadius: 10 }, Card: { headerFontSize: 16 } }
    }} getPopupContainer={trigger => trigger?.closest('.client-panel') || root.current || document.body}>
    <Layout ref={root} className={`client-panel${dark ? ' client-dark' : ''}`}>
      <a className="client-skip-link" href="#client-content">{t('Skip to content', { defaultValue: 'Skip to content' })}</a>
      {!mobile && <Sider width={260} collapsedWidth={80} collapsed={collapsed} className="client-sider">{navigation(collapsed)}</Sider>}
      <Drawer open={mobile && mobileOpen} onClose={() => setMobileOpen(false)} placement="left" width={Math.min(300, window.innerWidth - 24)}
        title={t('Menu', { defaultValue: 'Menu' })} getContainer={false} styles={{ body: { padding: 0 } }}>{navigation(false)}</Drawer>
      <Layout className="client-main" style={{ marginLeft: mobile ? 0 : collapsed ? 80 : 260 }}>
        <Header className="client-navbar">
          <div className="client-navbar-start"><Button type="text" icon={mobile || collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            aria-label={t('Toggle navigation', { defaultValue: 'Toggle navigation' })} aria-expanded={mobile ? mobileOpen : !collapsed}
            onClick={() => mobile ? setMobileOpen(true) : setCollapsed(!collapsed)} />
            <div className="client-navbar-title"><strong>{title}</strong></div>
          </div>
          <div className="client-navbar-actions">{themeControl(true)}
            <Dropdown trigger={['click']} menu={{ items: Object.entries(languages).map(([key, label]) => ({ key, label })),
              selectedKeys: [i18n.language?.split('-')[0]], onClick: ({ key }) => i18n.changeLanguage(key) }} placement="bottomRight">
              <Button type="text" icon={<GlobalOutlined />} aria-label={t('Language', { defaultValue: 'Language' })} />
            </Dropdown>
            <Button type="text" className="client-profile-button" onClick={() => handleTabChange('settings')} aria-label={t('Profile Settings')}>
              <Avatar src={userData.photo_profil} icon={<UserOutlined />} /><span>{userData.username}</span>
            </Button>
          </div>
        </Header>
        <Content id="client-content" tabIndex={-1} className="client-content">
          {activeTab !== 'overview' && <div className="client-page-heading"><span>MASTERFUT</span><h1>{title}</h1></div>}
          <div className={`client-page-body client-page-${activeTab}`}>{renderContent()}</div>
        </Content>
        <footer className="client-footer"><span>MasterFut</span><span>{t('Your game. Your next level.', { defaultValue: 'Your game. Your next level.' })}</span></footer>
      </Layout>
    </Layout>
  </ConfigProvider>;
}
